<template>
  <button
    :class="buttonClasses"
    :disabled="disabled"
    v-bind="$attrs"
   data-testid="components-ui-button-sk-button-button">
    <template v-if="loading">
      <SpinnerIcon
        class="animate-spin"
      data-testid="components-ui-button-sk-button-spinner-icon" />
    </template>
    <template v-else>
      <slot />
    </template>
  </button>
</template>

<script setup>
import { computed } from 'vue';
import SpinnerIcon from "@/components/ui/Button/SpinnerIcon.vue"

const props = defineProps({
  type: {
    type: String,
    default: 'primary',
    validator: (value) => {
      return ['primary', 'info', 'warning', 'danger'].includes(value)
    }
  },
  size: {
    type: String,
    default: 'lg',
    validator: (value) => {
      return ['sm', 'md', 'lg'].includes(value)
    }
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false
  },
  block: {
    type: Boolean,
    default: false
  }
});

const buttonClasses = computed(() => ({
  'sk-button': true,
  [`sk-button--size-${props.size}`]: true,
  [`sk-button--type-${props.type}`]: true,
  'sk-button--readonly': props.loading,
  'sk-button--disabled': props.disabled,
  'sk-button--block': props.block,
}));
</script>

<style lang="scss" scoped>
.sk-button {
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 600;
  font-family: Source Sans Pro;
  font-size: 16px;
  line-height: 19px;
  width: 100%;
  transition: all 0.3s;
  outline: none;

  &--size {
    &-sm {
      height: 26px;
      font-size: 12px;
      border-radius: 6px;
      max-width: 148px;
    }

    &-md {
      height: 36px;
      font-size: 14px;
      border-radius: 8px;
      max-width: 174px;
    }

    &-lg {
      height: 44px;
      border-radius: 10px;
      max-width: 292px;
    }
  }

  &--type {
    &-primary {
      background-color: #3A7CCA;
      color: #FFFFFF;

      &:hover {
        background-color: #2767B6;
      }

      &:hover.sk-button--readonly {
        background-color: #3A7CCA;
      }
    }

    &-info {
      background-color: #DBEBFF;
      color: #3A7CCA;

      &:hover {
        background-color: #C9E1FF;
      }

      &:hover.sk-button--readonly {
        background-color: #DBEBFF;
      }
    }

    &-warning {
      background-color: #FFF2EC;
      color: #C95726;

      &:hover {
        background-color: #FFE3D7;
      }

      &:hover.sk-button--readonly {
        background-color: #FFF2EC;
      }
    }

    &-danger {
      background-color: #D45050;
      color: #FFFFFF;

      &:hover {
        background-color: #C93131;
      }

      &:hover.sk-button--readonly {
        background-color: #D45050;
      }
    }
  }

  &--disabled {
    background-color: #DCE1E7;
    pointer-events: none;
    color: #A6AEB8;
    cursor: not-allowed;

    &:hover {
      background-color: #DCE1E7;
    }
  }

  &--readonly {
    cursor: default;
    pointer-events: none;
  }

  &--block {
    max-width: 100%;
  }
}

</style>
